/**
 * Cliente del hub OPUS (apps.opus.com.mx) desde el browser de OPUS ai.
 *
 * Habla directo al hub con credentials:'include' para mandar la cookie
 * `opus_session` (Domain=.opus.com.mx en prod; localhost entre puertos en dev).
 * NO usa `apiFetch`: un 401 del hub no debe cerrar la sesión de ai.
 */

export type HubApp = {
  id: string;
  label: string;
  url: string;
  live: boolean;
  soon: boolean;
  allowed: boolean;
  color: string;
};

export type HubClient = {
  name: string;
  email: string;
};

export type HubMe = {
  apps: HubApp[];
  client: HubClient | null;
  ia_plan: string | null;
  authenticated: boolean;
};

/**
 * Colores de cada app: los MISMOS que manda el hub (`APP_PRESENTATION` en
 * opus.com.mx/server/lib/apps.js) y que pintan logo y favicon (lib/brandingDelProducto):
 * ai azul, ci magenta (decisión de Sebastián), bi cian. Solo se usan si el hub no
 * responde o si una app llega sin color; lo que mande el hub manda.
 */
const APP_COLORS: Record<string, string> = {
  ai: '#0c6fd6',
  ci: '#D6299B',
  bi: '#06b6d4',
  ni: '#10b981',
  li: '#8b5cf6',
  di: '#f59e0b',
};

/**
 * URL de cada app cuando el hub no la da. En local (`localhost`, `127.0.0.1`, `*.localhost`)
 * apunta al stack de docker (ai y ci en :4800, bi en :4900, li en :8083); en cualquier otro
 * host, al subdominio de producción.
 */
export function staticAppUrl(id: string, hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''): string {
  const h = hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost')) {
    if (id === 'bi') return 'http://localhost:4900/';
    if (id === 'ci') return 'http://ci.localhost:4800/';
    if (id === 'li') return 'http://localhost:8083/';
    return 'http://localhost:4800/';
  }
  return `https://${prefijoDeEntorno(h)}${id}.opus.com.mx/`;
}

function staticApp(id: string): HubApp {
  return {
    id,
    label: `OPUS ${id}`,
    url: staticAppUrl(id),
    live: true,
    soon: false,
    allowed: true,
    color: APP_COLORS[id] ?? '#64748b',
  };
}

/** Lista fija (hub caído o sin sesión del hub): mismo orden que manda el hub. OPUS li entra en la 1.1.2. */
export const STATIC_HUB_APPS: HubApp[] = ['ai', 'ci', 'bi', 'li'].map(staticApp);

/**
 * Entorno por el dominio de la app: `pruebas.ai.opus.com.mx` → «pruebas.»; `ai.opus.com.mx` → «».
 * Así pruebas habla con el hub y las apps de pruebas aunque la imagen no traiga VITE_HUB_URL
 * (antes caía siempre en producción: otra sesión, sin créditos y la barra sin el hub).
 */
export function prefijoDeEntorno(hostname: string): string {
  const h = hostname.toLowerCase();
  return h.endsWith('.opus.com.mx') && h.startsWith('pruebas.') ? 'pruebas.' : '';
}

export function hubBaseUrl(hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''): string {
  const raw = String(import.meta.env.VITE_HUB_URL ?? '').trim();
  if (raw) return raw.replace(/\/+$/, '');
  if (import.meta.env.DEV) return 'http://localhost:4700';
  return `https://${prefijoDeEntorno(hostname)}apps.opus.com.mx`;
}

/** Vistas del hub. Hoy el front del hub no lee hash; #plan / #soporte son el contrato. */
export function hubPageUrl(page: HubPagina, tab?: string): string {
  const base = hubBaseUrl();
  // Mensajes (docs/29): el hub abre su panel con `?abrir=mensajes` (y la pestaña con `&tab=`).
  if (page === 'mensajes') return `${base}/?abrir=mensajes${tab ? `&tab=${encodeURIComponent(tab)}` : ''}`;
  if (page === 'soporte') return `${base}/#soporte`;
  if (page === 'plan' || page === 'facturacion') return `${base}/#plan`;
  return `${base}/`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function normalizeApp(raw: unknown): HubApp | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = String(r.id ?? '').toLowerCase();
  if (!id) return null;
  const fallback = STATIC_HUB_APPS.find((a) => a.id === id);
  const soon = r.soon === true || r.live === false || r.status === 'soon';
  const locked = r.locked === true;
  const allowedFlag = r.allowed;
  const allowed = allowedFlag === true || (allowedFlag !== false && !locked && !soon);
  return {
    id,
    label: String(r.label ?? r.name ?? fallback?.label ?? id),
    url: String(r.url ?? fallback?.url ?? ''),
    live: !soon,
    soon,
    allowed: allowed && !soon,
    color: String(r.color ?? fallback?.color ?? APP_COLORS[id] ?? '#64748b'),
  };
}

const GUEST_ME: HubMe = {
  apps: STATIC_HUB_APPS,
  client: null,
  ia_plan: null,
  authenticated: false,
};

/** Usuario con sesión en ESTA app (no en el hub). Se muestra si el hub no da sesión. */
export type LauncherAppUser = { name?: string | null; email?: string | null };

/** Texto del tooltip y del aria-label del tile (el tile ya no lleva etiqueta visible). */
export function tileTitle(app: Pick<HubApp, 'label' | 'soon' | 'allowed'>, current: boolean): string {
  if (current) return `${app.label} (actual)`;
  if (app.soon) return `${app.label} · Próximamente`;
  if (!app.allowed) return `${app.label} · Mejora tu plan`;
  return `Abrir ${app.label}`;
}

/** Quién aparece a la derecha: el cliente del hub o, sin sesión del hub, el usuario de la app. */
export function launcherWho(
  me: Pick<HubMe, 'client' | 'authenticated'>,
  appUser: LauncherAppUser | null,
): { label: string; mail: string; showLogin: boolean } {
  const fuente = me.authenticated && me.client ? me.client : appUser;
  const name = (fuente?.name ?? '').trim();
  const email = (fuente?.email ?? '').trim();
  const label = name || email;
  return {
    label,
    mail: email && email !== label ? email : '',
    showLogin: !me.authenticated && !label,
  };
}

export async function fetchHubMe(signal?: AbortSignal): Promise<HubMe> {
  try {
    const res = await fetch(`${hubBaseUrl()}/api/client/me`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (res.status === 401 || !res.ok) return GUEST_ME;
    const data = asRecord(await res.json());
    if (!data) return GUEST_ME;
    const apps = (Array.isArray(data.apps) ? data.apps : [])
      .map(normalizeApp)
      .filter((a): a is HubApp => a !== null);
    const c = asRecord(data.client);
    return {
      apps: apps.length > 0 ? apps : STATIC_HUB_APPS,
      client: c
        ? { name: String(c.name ?? ''), email: String(c.user ?? c.email ?? '') }
        : null,
      ia_plan: data.ia_plan == null ? null : String(data.ia_plan),
      authenticated: true,
    };
  } catch (err) {
    if (signal?.aborted) throw err;
    return GUEST_ME;
  }
}

/** Fondo del ícono de una app: degradado del color hacia uno más oscuro (como su logo). */
export function fondoApp(color: string): string {
  return `linear-gradient(135deg, ${color}, color-mix(in srgb, ${color} 70%, #000))`;
}

/** Origen del hub (para aceptar sus mensajes cuando la app corre embebida). */
export function hubOrigin(): string {
  try {
    return new URL(hubBaseUrl()).origin;
  } catch {
    return '';
  }
}

export async function fetchSsoTicket(app: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch(`${hubBaseUrl()}/api/client/sso-ticket`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ app }),
      signal,
    });
    if (!res.ok) return null;
    const data = asRecord(await res.json());
    const url = data && typeof data.url === 'string' ? data.url.trim() : '';
    return url || null;
  } catch {
    return null;
  }
}

export type HubPagina = 'home' | 'plan' | 'soporte' | 'facturacion' | 'mensajes';

/** Navegación de la pestaña (un objeto para poder espiarla en pruebas: jsdom no navega). */
export const navegacion = {
  ir(url: string): void {
    window.location.assign(url);
  },
};

/** Mensaje de la barra OPUS al hub cuando la app corre embebida (solo al origen del hub). */
export type MensajeAlHub =
  | { tipo: 'opus-hub:hola' }
  | { tipo: 'opus-hub:abrir'; app: string }
  | { tipo: 'opus-hub:prefetch'; app: string }
  | { tipo: 'opus-hub:ir'; pagina: HubPagina; tab?: string }
  | { tipo: 'opus-hub:barra'; abierta: boolean };

export function enviarAlHub(msg: MensajeAlHub, win: Pick<Window, 'parent'> = window): void {
  const origen = hubOrigin();
  if (!origen) return;
  try {
    win.parent.postMessage(msg, origen);
  } catch {
    /* sin padre accesible: nada que hacer */
  }
}

/**
 * Ir a una vista del hub (Mis aplicaciones, Mi plan, Soporte, Facturación) SIN pestaña nueva:
 * dentro del hub se lo pide al hub (cierra la app y muestra la vista); en su propio sitio
 * navega en esta misma pestaña.
 */
export function irAlHub(pagina: HubPagina, enHub: boolean, tab?: string): void {
  if (enHub) {
    enviarAlHub(tab ? { tipo: 'opus-hub:ir', pagina, tab } : { tipo: 'opus-hub:ir', pagina });
    return;
  }
  navegacion.ir(hubPageUrl(pagina, tab));
}

/**
 * Caché corta de tickets SSO por app. El ticket es de UN SOLO USO (TTL 60 s en eko27):
 * `prefetch` lo pide por adelantado (al pasar el mouse o el foco por un tile) sin pedir dos
 * veces; `tomar` entrega el adelantado y lo saca (el siguiente será otro) o pide uno nuevo si
 * no hay, venció o el adelantado falló. Misma lógica que el hub (opus.com.mx/public/hub-apps.js).
 */
export function crearCacheTickets(
  pedir: (app: string) => Promise<string | null>,
  { ttlMs = 40_000, ahora = () => Date.now() }: { ttlMs?: number; ahora?: () => number } = {},
) {
  type Entrada = { at: number; promesa: Promise<string | null> };
  const cache = new Map<string, Entrada>();
  const vigente = (e: Entrada | undefined): e is Entrada => !!e && ahora() - e.at < ttlMs;
  const lanzar = (app: string): Entrada => {
    const e: Entrada = { at: ahora(), promesa: Promise.resolve(null) };
    e.promesa = pedir(app).then(
      (url) => {
        // Un fallo no se guarda: el siguiente intento vuelve a pedir.
        if (!url && cache.get(app) === e) cache.delete(app);
        return url;
      },
      () => {
        if (cache.get(app) === e) cache.delete(app);
        return null;
      },
    );
    return e;
  };
  return {
    prefetch(app: string): Promise<string | null> {
      const e = cache.get(app);
      if (vigente(e)) return e.promesa;
      const nueva = lanzar(app);
      cache.set(app, nueva);
      return nueva.promesa;
    },
    tomar(app: string): Promise<string | null> {
      const e = cache.get(app);
      cache.delete(app);
      if (!vigente(e)) return lanzar(app).promesa;
      return e.promesa.then((url) => url ?? lanzar(app).promesa);
    },
    tiene(app: string): boolean {
      return vigente(cache.get(app));
    },
    limpiar(): void {
      cache.clear();
    },
  };
}

/** Tickets del launcher de esta pestaña. */
export const ticketsSso = crearCacheTickets((app) => fetchSsoTicket(app));

/**
 * Cambia a otra app EN ESTA PESTAÑA (sin abrir otra): espera la URL (ticket SSO, el adelantado
 * si hay) y navega. Mientras tanto la barra muestra el tile girando y la barra de progreso; el
 * fondo del tema ya está pintado en el index.html de la app destino (sin destello blanco).
 */
export async function navegarAApp(
  obtenerUrl: () => Promise<string | null>,
  nav: (url: string) => void = (url) => navegacion.ir(url),
): Promise<boolean> {
  const url = await obtenerUrl();
  if (!url) return false;
  nav(url);
  return true;
}
